import type {
  GlobalRolePermissions,
  MspPortalPermissions,
  WorkspaceRolePermissions,
} from "@/_store/roles.module.ts";
import { defineStore } from "pinia";
import isEmpty from "lodash/isEmpty";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https.ts";
import { StoreModule } from "@/constants/store.ts";

export interface PermissionsState {
  currentWorkspacePermissions: WorkspaceRolePermissions | null;
  globalWorkspacePermissions: GlobalRolePermissions | null;
  mspPortalPermissions: MspPortalPermissions | null;
}

export type UserPermissionsResponse = {
  localRole: WorkspaceRolePermissions | null;
  mspRole: MspPortalPermissions | null;
  globalRole: GlobalRolePermissions | null;
};

export const usePermissionsStore = defineStore(StoreModule.PERMISSIONS, {
  getters: {
    /**
     * Checks if the user has Global Admin permissions.
     * Derived by checking if the global permissions object is non-null and not empty.
     * @param state - The current store state.
     * @returns True if global permissions exist, false otherwise.
     */
    isGlobalAdmin(state) {
      return !isEmpty(state.globalWorkspacePermissions);
    },

    /**
     * Checks if the user has MSP (Managed Service Provider) permissions.
     * Derived by checking if the MSP permissions object is non-null and not empty.
     * @param state - The current store state.
     * @returns True if MSP permissions exist, false otherwise.
     */
    hasMspPermissions(state) {
      return !isEmpty(state.mspPortalPermissions);
    },
  },
  state: (): PermissionsState => ({
    currentWorkspacePermissions: null,
    globalWorkspacePermissions: null,
    mspPortalPermissions: null,
  }),
  persist: true,
  actions: {
    /**
     * Fetches user permissions for a specific workspace.
     *
     * This action retrieves Local, MSP, and Global roles. It updates the store state
     * on success
     *
     * @param {string} workspaceId - The unique identifier of the workspace to fetch permissions for.
     * @throws {Error} Throws an error if the API request fails, ensuring the caller is aware of the failure.
     * @returns {Promise<void>} A promise that resolves when permissions are successfully updated.
     */
    async getUserPermissions(workspaceId: string): Promise<void> {
      if (!workspaceId) {
        console.error("Unable to fetch permissions: Workspace ID is missing.");
        return;
      }

      try {
        const { data } = await axiosInstance.request<UserPermissionsResponse>(
          api.getUserPermissions(workspaceId)
        );

        this.currentWorkspacePermissions = data.localRole;
        this.mspPortalPermissions = data.mspRole;
        this.globalWorkspacePermissions = data.globalRole;
      } catch (error: unknown) {
        console.error(`Failed to fetch permissions for workspace "${workspaceId}":`, error);
      }
    },
  },
});
